import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const getCurrentUser = () => localStorage.getItem('notes_user') || '';

async function fetchWithAuth(url, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${url}`, { ...options, headers: { ...options.headers, 'x-token': token } });
  if (res.status === 401) { localStorage.removeItem('notes_token'); localStorage.removeItem('notes_user'); alert('登录超时，请重新登录'); window.location.href = '/'; return null; }
  return res;
}

const PRIORITY_LIST = ['高', '中', '低'];

export default function TaskCreate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tplInputRef = useRef();
  
  const parentId = searchParams.get('parentId');
  const level = searchParams.get('level') ? Number(searchParams.get('level')) : 1;

  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tplUploading, setTplUploading] = useState(false);
  const [uploadedTemplates, setUploadedTemplates] = useState([]);

  const [taskForm, setTaskForm] = useState({
    name: '',
    assignee: getCurrentUser(),
    members: [],
    plannedStart: '',
    plannedEnd: '',
    priority: '中',
    warningDays: 3,
    warningTime: '09:00',
    noWarning: false,
    isRecurring: false,
    recurringRule: '',
    remarks: '',
    isPersonal: false,
    currentPeriod: 1
  });

  useEffect(() => { fetchProject(); fetchUsers(); }, [id]);

  const fetchProject = async () => {
    const res = await fetchWithAuth(`/api/projects/${id}`);
    if (!res) return;
    setProject(await res.json());
    setLoading(false);
  };

  const fetchUsers = async () => {
    const res = await fetchWithAuth('/api/auth/users');
    if (!res) return;
    const data = await res.json();
    setUsers(data.users || data || []);
  };

  const handleUploadTemplate = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setTplUploading(true);
    try {
      // 暂存本地文件，创建后统一上传
      setUploadedTemplates(prev => [...prev, { file, name: file.name, size: file.size }]);
    } catch (err) { alert('上传失败'); }
    setTplUploading(false);
    e.target.value = '';
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!taskForm.name.trim()) { alert('请输入任务名称'); return; }
    setSaving(true);
    try {
      const res = await fetchWithAuth(`/api/projects/${id}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...taskForm, parentId, level })
      });
      const data = await res.json();
      if (data.error) { alert(data.error); setSaving(false); return; }
      
      // 为每个创建的任务上传模板
      if (uploadedTemplates.length > 0 && data.length > 0) {
        for (const task of data) {
          for (const tpl of uploadedTemplates) {
            const formData = new FormData();
            formData.append('file', tpl.file);
            await fetchWithAuth(`/api/tasks/${task.id}/templates`, { method: 'POST', body: formData });
          }
        }
      }
      
      alert('创建成功');
      navigate(-1);
    } catch (err) { alert('创建失败'); }
    setSaving(false);
  };

  if (loading) return <div className="loading-state">加载中...</div>;
  if (!project) return <div className="empty-state">项目不存在</div>;

  return (
    <div className="page-container">
      {/* 顶部导航 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn-secondary" onClick={() => navigate(-1)} style={{ padding: '4px 12px' }}>← 返回</button>
        <span style={{ color: '#999' }}>{project.name}</span>
        <span style={{ color: '#ccc' }}>|</span>
        <span style={{ fontWeight: 500 }}>创建{level > 1 ? '子任务' : '阶段'}</span>
      </div>

      {/* 表单 */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: 24 }}>
        <form onSubmit={handleCreate}>
          {/* 任务名称 */}
          <div className="form-group">
            <label>任务名称 *</label>
            <input
              type="text"
              value={taskForm.name}
              onChange={e => setTaskForm({ ...taskForm, name: e.target.value })}
              className="form-input"
              autoFocus
              placeholder="输入任务名称"
            />
          </div>

          {/* 备注 */}
          <div className="form-group">
            <label>备注</label>
            <textarea
              value={taskForm.remarks || ''}
              onChange={e => setTaskForm({ ...taskForm, remarks: e.target.value })}
              className="form-input"
              rows={3}
              placeholder="任务背景、要求、注意事项等"
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* 个人目标任务 */}
          <div className="form-group" style={{ marginTop: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={taskForm.isPersonal || false}
                onChange={e => setTaskForm({ ...taskForm, isPersonal: e.target.checked })}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ fontWeight: 500 }}>个人目标任务</span>
            </label>
            {taskForm.isPersonal && <div style={{ marginTop: 4, color: '#999', fontSize: 13 }}>勾选后，将为选中的每个成员各创建一份独立任务</div>}
          </div>

          {/* 负责人 & 优先级 */}
          <div className="form-row">
            <div className="form-group">
              <label>负责人</label>
              <select value={taskForm.assignee} onChange={e => setTaskForm({ ...taskForm, assignee: e.target.value })} className="form-input">
                {users.map(u => <option key={u.username} value={u.username}>{u.realname}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>优先级</label>
              <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })} className="form-input">
                {PRIORITY_LIST.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* 任务成员 */}
          <div className="form-group">
            <label>任务成员（可多选）</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
              {users.map(u => {
                const members = taskForm.members || [];
                const isChecked = members.includes(u.username);
                return (
                  <label key={u.username} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={e => {
                        const newMembers = e.target.checked ? [...members, u.username] : members.filter(m => m !== u.username);
                        setTaskForm({ ...taskForm, members: newMembers });
                      }}
                      style={{ width: 16, height: 16 }}
                    />
                    <span>{u.realname}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* 计划时间 */}
          <div className="form-row">
            <div className="form-group">
              <label>计划开始</label>
              <input type="date" value={taskForm.plannedStart} onChange={e => setTaskForm({ ...taskForm, plannedStart: e.target.value })} className="form-input" />
            </div>
            <div className="form-group">
              <label>计划结束</label>
              <input type="date" value={taskForm.plannedEnd} onChange={e => setTaskForm({ ...taskForm, plannedEnd: e.target.value })} className="form-input" />
            </div>
          </div>

          {/* 预警设置 */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={taskForm.noWarning} onChange={e => setTaskForm({ ...taskForm, noWarning: e.target.checked })} style={{ width: 16, height: 16 }} />
              不发送预警
            </label>
          </div>
          {!taskForm.noWarning && (
            <div className="form-row">
              <div className="form-group">
                <label>提前预警天数</label>
                <input type="number" min="0" value={taskForm.warningDays} onChange={e => setTaskForm({ ...taskForm, warningDays: Number(e.target.value) })} className="form-input" />
              </div>
              <div className="form-group">
                <label>预警时间</label>
                <input type="time" value={taskForm.warningTime || '09:00'} onChange={e => setTaskForm({ ...taskForm, warningTime: e.target.value })} className="form-input" />
                <small style={{ color: '#999', fontSize: '11px' }}>每天几点发送预警</small>
              </div>
            </div>
          )}

          {/* 周期任务 */}
          <div className="form-row">
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={taskForm.isRecurring} onChange={e => setTaskForm({ ...taskForm, isRecurring: e.target.checked })} style={{ width: 16, height: 16 }} />
                周期任务
              </label>
            </div>
          </div>
          {taskForm.isRecurring && (
            <div className="form-row">
              <div className="form-group">
                <label>周期规则</label>
                <select value={taskForm.recurringRule} onChange={e => setTaskForm({ ...taskForm, recurringRule: e.target.value })} className="form-input">
                  <option value="">请选择周期</option>
                  <option value="weekly-1">每周一</option>
                  <option value="weekly-2">每周二</option>
                  <option value="weekly-3">每周三</option>
                  <option value="weekly-4">每周四</option>
                  <option value="weekly-5">每周五</option>
                  <option value="monthly-1">每月1号</option>
                  <option value="monthly-15">每月15号</option>
                  <option value="monthly-last">每月最后一天</option>
                </select>
              </div>
            </div>
          )}
          {taskForm.isRecurring && taskForm.isPersonal && (
            <div className="form-row">
              <div className="form-group">
                <label>初始期数</label>
                <input type="number" min="1" value={taskForm.currentPeriod || 1} onChange={e => setTaskForm({ ...taskForm, currentPeriod: Number(e.target.value) })} className="form-input" />
                <small style={{ color: '#999', fontSize: 11 }}>周期任务从第N期开始记录</small>
              </div>
            </div>
          )}

          {/* 模板上传 */}
          <div className="form-group">
            <label>任务模板（可多选）</label>
            <div style={{ marginTop: 8 }}>
              <label style={{ padding: '6px 16px', background: '#722ed1', color: '#fff', borderRadius: 6, cursor: tplUploading ? 'not-allowed' : 'pointer', fontSize: 13, display: 'inline-block' }}>
                {tplUploading ? '上传中...' : '+ 选择模板文件'}
                <input type="file" ref={tplInputRef} style={{ display: 'none' }} onChange={handleUploadTemplate} disabled={tplUploading} />
              </label>
              {uploadedTemplates.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {uploadedTemplates.map((tpl, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f9f0ff', borderRadius: 6, border: '1px solid #d3adf7' }}>
                      <span style={{ color: '#722ed1' }}>📄 {tpl.name}</span>
                      <span style={{ color: '#999', fontSize: 12 }}>{(tpl.size / 1024).toFixed(1)}KB</span>
                      <button type="button" onClick={() => setUploadedTemplates(prev => prev.filter((_, idx) => idx !== i))} style={{ color: '#ff4d4f', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>删除</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 提交按钮 */}
          <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
            <button type="submit" className="btn-primary" disabled={saving} style={{ padding: '10px 32px' }}>
              {saving ? '创建中...' : `创建${level > 1 ? '子任务' : '阶段'}`}
            </button>
            <button type="button" className="btn-secondary" onClick={() => navigate(-1)} style={{ padding: '10px 32px' }}>取消</button>
          </div>
        </form>
      </div>
    </div>
  );
}
